<template>
  <div class="cut-workspace">
    <b-card v-if="!cut" class="text-center py-4">
      <b-spinner variant="secondary" small /> <span class="ml-2">Cargando corte…</span>
    </b-card>

    <template v-else>
      <b-card body-class="cut-workspace__header-body">
        <div class="cut-workspace__header">
          <div class="cut-workspace__title">
            <b-button variant="link" class="list-view-icon-btn" title="Volver a los cortes" aria-label="Volver a los cortes" @click="$emit('close')">
              <AppIcon name="arrow_back" />
            </b-button>
            <div>
              <h4 class="mb-0">Corte #{{ cut.id }} <span class="cut-workspace__status" :class="'cut-workspace__status--' + cut.status">{{ statusLabel(cut.status) }}</span></h4>
              <div class="cut-workspace__meta">Pedidos recibidos hasta {{ formatDate(cut.cutoff_at) }} · creado {{ formatDate(cut.created_at) }}</div>
            </div>
          </div>
          <div class="cut-workspace__header-actions">
            <b-button v-if="showDownload && primary && primary.id !== 'download'" size="sm" variant="outline-dark" :disabled="busy" @click="downloadZip">
              <AppIcon name="download" class="mr-1" /> Comprobantes
            </b-button>
            <b-button v-if="primary" size="sm" variant="dark" :disabled="busy || primary.disabled" :title="primary.hint || ''" @click="primary.action()">
              <AppIcon :name="primary.icon" class="mr-1" /> {{ primary.label }}
            </b-button>
            <b-dropdown right no-caret size="sm" variant="link" boundary="window" toggle-class="list-view-icon-btn">
              <template #button-content><AppIcon name="more_vert" label="Más acciones" /></template>
              <b-dropdown-item @click="$emit('print', cut.id, 'order-cut-preparation')"><AppIcon name="print" /> Hoja de preparación (por producto)</b-dropdown-item>
              <b-dropdown-item @click="$emit('print', cut.id, 'order-cut-orders')"><AppIcon name="summarize" /> Pedidos por vendedor y zona</b-dropdown-item>
              <b-dropdown-item v-if="cut.can.add_orders" @click="openAddOrders"><AppIcon name="add_shopping_cart" /> Agregar pedidos</b-dropdown-item>
              <b-dropdown-item v-if="cut.can.reopen" @click="reopen"><AppIcon name="undo" /> Reabrir preparación</b-dropdown-item>
              <b-dropdown-item v-if="canRebuildZip" @click="rebuildZip"><AppIcon name="refresh" /> Volver a generar ZIP</b-dropdown-item>
              <template v-if="cut.can.delete">
                <b-dropdown-divider />
                <b-dropdown-item @click="removeCut"><AppIcon name="delete" class="text-danger" /> Eliminar corte</b-dropdown-item>
              </template>
            </b-dropdown>
          </div>
        </div>

        <ol class="cut-steps">
          <li v-for="step in steps" :key="step.key" class="cut-steps__item" :class="'cut-steps__item--' + step.state">
            <span class="cut-steps__marker"><AppIcon v-if="step.state === 'done'" name="check" /><template v-else>{{ step.number }}</template></span>
            <span class="cut-steps__text"><strong>{{ step.label }}</strong><small>{{ step.detail }}</small></span>
          </li>
        </ol>
      </b-card>

      <div class="cut-kpis">
        <div v-for="kpi in kpis" :key="kpi.key" class="cut-kpi" :class="'cut-kpi--' + kpi.tone">
          <span class="cut-kpi__icon"><AppIcon :name="kpi.icon" /></span>
          <span class="cut-kpi__body">
            <span class="cut-kpi__label">{{ kpi.label }}</span>
            <strong class="cut-kpi__value">{{ kpi.value }}</strong>
            <span v-if="kpi.progress !== undefined" class="cut-kpi__bar"><span :style="{ width: kpi.progress + '%' }" /></span>
          </span>
        </div>
      </div>

      <div v-if="cut.status === 'preparing' && !cut.totals.orders_ready && cut.totals.orders_waiting_weight" class="app-alert app-alert--warning mb-3">
        <span class="app-alert__icon"><AppIcon name="scale" /></span>
        <span class="app-alert__content">
          Solo faltan los pesables: {{ cut.totals.pending_weighable }} {{ cut.totals.pending_weighable === 1 ? 'producto' : 'productos' }} en {{ cut.totals.orders_waiting_weight }} pedidos.
          Abrí cada pedido, cargá el peso de la balanza en el campo amarillo y después controlalo.
          <b-button variant="link" class="list-view-link p-0 ml-1" @click="view = 'weighable'">Ver solo pedidos a pesar</b-button>
        </span>
      </div>

      <div v-if="invoiceRunning" class="app-alert app-alert--info mb-3">
        <span class="app-alert__icon"><b-spinner small /></span>
        <span class="app-alert__content">Facturando en segundo plano: {{ cut.totals.invoices_done }} de {{ cut.totals.orders }} pedidos. Podés salir de esta pantalla y volver más tarde.</span>
      </div>
      <div v-if="!invoiceRunning && cut.totals.invoices_error" class="app-alert app-alert--danger mb-3">
        <span class="app-alert__icon"><AppIcon name="error" /></span>
        <span class="app-alert__content">
          {{ cut.totals.invoices_error }} {{ cut.totals.invoices_error === 1 ? 'pedido no se pudo facturar' : 'pedidos no se pudieron facturar' }}.
          Los demás siguen su curso: reintentá o quitá del corte los que necesiten resolverse aparte.
          <b-button variant="link" class="list-view-link p-0 ml-1" @click="view = 'error'">Ver solo errores</b-button>
        </span>
      </div>
      <div v-if="zipRunning" class="app-alert app-alert--info mb-3">
        <span class="app-alert__icon"><b-spinner small /></span>
        <span class="app-alert__content">Armando el ZIP de comprobantes…</span>
      </div>
      <div v-if="cut.zip_status === 'error' && cut.zip_error" class="app-alert app-alert--danger mb-3">
        <span class="app-alert__icon"><AppIcon name="error" /></span>
        <span class="app-alert__content">No se pudo armar el ZIP: {{ cut.zip_error }}</span>
      </div>

      <b-card no-body class="cut-workspace__orders">
        <div class="cut-workspace__toolbar">
          <div class="list-view-filter cut-workspace__search">
            <b-input-group size="sm">
              <b-input-group-prepend is-text><AppIcon name="search" /></b-input-group-prepend>
              <b-form-input v-model="search" placeholder="Buscar cliente o nº de pedido" />
            </b-input-group>
          </div>
          <div class="list-view-filter cut-workspace__seller">
            <b-form-select v-model="sellerFilter" :options="sellerOptions" size="sm" />
          </div>
          <div class="cut-workspace__views" role="group" aria-label="Filtrar pedidos">
            <button v-for="option in viewOptions" :key="option.value" type="button"
                    class="cut-workspace__view" :class="{ 'cut-workspace__view--active': view === option.value }"
                    @click="view = option.value">
              <AppIcon :name="option.icon" /> {{ option.text }} <span class="cut-workspace__view-count" :class="option.count && option.tone ? 'cut-workspace__view-count--' + option.tone : ''">{{ option.count }}</span>
            </button>
          </div>
          <div class="cut-workspace__fold ml-auto">
            <b-button v-if="expandedCount" variant="link" class="list-view-link" @click="expanded = {}"><AppIcon name="unfold_less" /> Cerrar pedidos</b-button>
            <b-button variant="link" class="list-view-link" @click="toggleAllSellers">
              <AppIcon :name="allSellersCollapsed ? 'unfold_more' : 'unfold_less'" /> {{ allSellersCollapsed ? 'Expandir vendedores' : 'Contraer vendedores' }}
            </b-button>
          </div>
        </div>

        <EmptyState v-if="!groups.length" icon="inventory_2"
                    :title="cut.totals.orders ? 'Ningún pedido coincide' : 'El corte no tiene pedidos'"
                    :description="cut.totals.orders ? 'Probá con otra búsqueda o filtro.' : 'Agregá pedidos o eliminá el corte.'" />

        <section v-for="group in groups" :key="group.seller" class="cut-group">
          <header class="cut-group__seller" role="button" tabindex="0" @click="toggleSeller(group.seller)" @keydown.enter="toggleSeller(group.seller)">
            <AppIcon :name="collapsedSellers[group.seller] ? 'chevron_right' : 'expand_more'" class="cut-group__chevron" />
            <b-avatar size="1.8rem" :text="avatar(group.seller).text" :style="avatar(group.seller).style" />
            <span class="cut-group__name">{{ group.seller }}</span>
            <span class="cut-group__summary">
              <span v-if="group.errors" class="cut-group__pill cut-group__pill--danger"><AppIcon name="error" /> {{ group.errors }}</span>
              <span v-if="group.weighable" class="cut-group__pill cut-group__pill--warning"><AppIcon name="scale" /> {{ group.weighable }}</span>
              <span class="cut-group__pill" :class="group.ready === group.count ? 'cut-group__pill--success' : ''">
                <AppIcon name="check_circle" /> {{ group.ready }}/{{ group.count }} {{ group.count === 1 ? 'pedido' : 'pedidos' }}
              </span>
            </span>
          </header>
          <div v-for="place in group.places" v-show="!collapsedSellers[group.seller]" :key="place.key">
            <div class="cut-group__place"><AppIcon name="location_on" /> {{ place.zone }} <span>›</span> {{ place.city }}</div>
            <OrderCutOrder v-for="order in place.orders" :key="order.id"
                           :cut="cut" :order="order" :expanded="!!expanded[order.id]"
                           :weight-units="cut.weight_units" :revision="revision" :is-mobile="isMobile" :busy="busy"
                           @toggle="toggleOrder" @updated="onLineUpdated"
                           @control="controlOrder" @reopen="reopenOrder" @remove="removeOrder" />
          </div>
        </section>
      </b-card>
    </template>

    <b-modal v-model="addModal.open" size="lg" scrollable centered @hidden="addModal.selected = []">
      <div slot="modal-header" class="app-modal-header">
        <span class="app-modal-header__icon"><AppIcon name="add_shopping_cart" /></span>
        <h5 class="app-modal-header__title">Agregar pedidos al corte #{{ cut ? cut.id : '' }}</h5>
      </div>
      <p class="text-muted small mb-2">Pedidos cerrados que todavía no están en ningún corte. Se agregan sin respetar la hora de corte.</p>
      <b-input-group size="sm" class="mb-2">
        <b-input-group-prepend is-text><AppIcon name="search" /></b-input-group-prepend>
        <b-form-input v-model="addModal.search" placeholder="Buscar cliente o nº de pedido" @keyup.enter.native="loadEligible(1)" />
        <b-input-group-append><b-button variant="outline-dark" @click="loadEligible(1)">Buscar</b-button></b-input-group-append>
      </b-input-group>
      <div v-if="addModal.loading" class="text-center py-3"><b-spinner small variant="secondary" /></div>
      <EmptyState v-else-if="!addModal.items.length" icon="assignment" title="No hay pedidos disponibles" description="No quedan pedidos cerrados fuera de un corte con esa búsqueda." />
      <b-table v-else class="app-table app-table--compact mb-0" :items="addModal.items" :fields="addFields" small hover @row-clicked="toggleAddSelection">
        <template v-slot:cell(select)="data">
          <span class="cut-workspace__check" role="checkbox" :aria-checked="addModal.selected.includes(data.item.id) ? 'true' : 'false'" @click.stop="toggleAddSelection(data.item)">
            <AppIcon :name="addModal.selected.includes(data.item.id) ? 'check_box' : 'check_box_outline_blank'" :fill="addModal.selected.includes(data.item.id)"
                     :class="addModal.selected.includes(data.item.id) ? 'text-dark' : 'text-secondary'" />
          </span>
        </template>
        <template v-slot:cell(id)="data"><strong>#{{ data.item.id }}</strong></template>
        <template v-slot:cell(customer_name)="data">{{ data.item.customer_name || 'Sin cliente' }}</template>
        <template v-slot:cell(place)="data">{{ data.item.zone_name || 'Sin zona' }} › {{ data.item.city || 'Sin ciudad' }}</template>
        <template v-slot:cell(created_at)="data">{{ formatDate(data.item.created_at) }}</template>
      </b-table>
      <b-pagination v-if="addModal.total > addModal.perPage" v-model="addModal.page" :total-rows="addModal.total" :per-page="addModal.perPage"
                    size="sm" pills class="mt-2 mb-0" @input="loadEligible($event)" />
      <template #modal-footer>
        <b-button size="sm" variant="outline-secondary" @click="addModal.open = false">Cancelar</b-button>
        <b-button size="sm" variant="dark" :disabled="busy || !addModal.selected.length" @click="addOrders">
          Agregar {{ addModal.selected.length || '' }} {{ addModal.selected.length === 1 ? 'pedido' : 'pedidos' }}
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import serviceAPI from '../services'
import Error from '@/handler/error'
import OrderCutOrder from './OrderCutOrder'
import Funciones from '@/handler/funciones'

const STATUS_LABELS = { preparing: 'En preparación', prepared: 'Preparado', ready: 'Listo para reparto', on_route: 'En reparto', finished: 'Finalizado' }

export default {
  name: 'OrderCutWorkspace',
  components: { OrderCutOrder },
  props: {
    cutId: { type: Number, required: true },
    isMobile: { type: Boolean, default: false },
  },
  data() {
    return {
      cut: null,
      busy: false,
      search: '',
      sellerFilter: null,
      view: 'all',
      expanded: {},
      collapsedSellers: {},
      revision: 0,
      refreshTimer: null,
      addModal: { open: false, search: '', items: [], selected: [], page: 1, total: 0, perPage: 50, loading: false },
      addFields: [
        { key: 'select', label: '', class: 'text-center', thStyle: { width: '36px' } },
        { key: 'id', label: 'Pedido' },
        { key: 'customer_name', label: 'Cliente' },
        { key: 'seller_name', label: 'Vendedor' },
        { key: 'place', label: 'Zona › Ciudad' },
        { key: 'created_at', label: 'Recibido' },
      ],
    }
  },
  computed: {
    invoiceRunning() { return this.cut && this.cut.totals.invoices_running > 0 },
    zipRunning() { return this.cut && ['pending', 'processing'].includes(this.cut.zip_status) },
    showDownload() { return this.cut && this.cut.has_zip && !this.zipRunning },
    canRebuildZip() { return this.cut && this.cut.invoicing_finished_at && this.cut.totals.invoices_done > 0 && !this.zipRunning && !this.invoiceRunning },
    expandedCount() { return Object.keys(this.expanded).filter(id => this.expanded[id]).length },
    // Una sola acción principal: la del paso en el que está el corte.
    primary() {
      if(!this.cut) return null
      const totals = this.cut.totals
      if(this.cut.status === 'preparing') {
        if(totals.orders_ready > 0) {
          return { id: 'control', icon: 'done_all', label: 'Controlar ' + totals.orders_ready + ' pedidos', action: () => this.controlAll() }
        }
        return {
          id: 'complete', icon: 'task_alt', label: 'Finalizar preparación', action: () => this.complete(),
          disabled: !this.cut.can.complete,
          hint: totals.orders_waiting_weight ? totals.orders_waiting_weight + ' pedidos esperan el peso de sus pesables' : '',
        }
      }
      if(this.cut.can.invoice) {
        const retry = totals.invoices_error > 0
        return {
          id: 'invoice', icon: 'receipt_long', action: () => this.invoice(),
          label: retry ? 'Reintentar ' + totals.invoices_error + ' con error' : 'Facturar ' + totals.orders + ' pedidos',
        }
      }
      if(this.showDownload) return { id: 'download', icon: 'download', label: 'Descargar comprobantes', action: () => this.downloadZip() }
      return null
    },
    steps() {
      if(!this.cut) return []
      const t = this.cut.totals
      const status = this.cut.status
      const prepared = status !== 'preparing'
      const invoiced = ['ready', 'on_route', 'finished'].includes(status)
      return [
        { key: 'select', number: 1, label: 'Selección', detail: t.orders + ' pedidos', state: 'done' },
        { key: 'prepare', number: 2, label: 'Preparación', detail: t.orders_controlled + ' de ' + t.orders + ' pedidos controlados', state: prepared ? 'done' : 'current' },
        { key: 'invoice', number: 3, label: 'Facturación', detail: t.invoices_done + ' de ' + t.orders + ' facturados' + (t.invoices_error ? ' · ' + t.invoices_error + ' con error' : ''), state: invoiced ? 'done' : (prepared ? 'current' : 'pending') },
        { key: 'zip', number: 4, label: 'Comprobantes', detail: this.cut.has_zip ? 'ZIP listo' + (invoiced ? '' : ' (parcial)') : (this.zipRunning ? 'Armando ZIP…' : 'Sin generar'), state: this.cut.has_zip && invoiced ? 'done' : (this.cut.has_zip || this.zipRunning ? 'current' : 'pending') },
      ]
    },
    kpis() {
      if(!this.cut) return []
      const t = this.cut.totals
      const percent = (value, total) => total ? Math.round(value * 100 / total) : 0
      return [
        { key: 'orders', icon: 'inventory_2', tone: 'neutral', label: 'Pedidos', value: t.orders },
        { key: 'controlled', icon: 'verified', tone: t.orders && t.orders_controlled === t.orders ? 'success' : 'primary', label: 'Pedidos controlados', value: t.orders_controlled + ' / ' + t.orders, progress: percent(t.orders_controlled, t.orders) },
        { key: 'weigh', icon: 'scale', tone: t.pending_weighable ? 'warning' : 'neutral', label: 'Pesables pendientes', value: t.pending_weighable },
        { key: 'invoices', icon: 'receipt_long', tone: t.invoices_done && t.invoices_done === t.orders ? 'success' : 'primary', label: 'Facturados', value: t.invoices_done + ' / ' + t.orders, progress: percent(t.invoices_done, t.orders) },
        { key: 'errors', icon: 'error', tone: t.invoices_error ? 'danger' : 'neutral', label: 'Con error', value: t.invoices_error },
      ]
    },
    allSellersCollapsed() { return !!this.groups.length && this.groups.every(group => this.collapsedSellers[group.seller]) },
    sellerOptions() {
      const names = this.cut ? Array.from(new Set(this.cut.orders.map(order => order.seller_name))) : []
      return [{ value: null, text: 'Todos los vendedores' }].concat(names.map(name => ({ value: name, text: name })))
    },
    viewOptions() {
      const orders = this.cut ? this.cut.orders : []
      const count = filter => orders.filter(filter).length
      const options = [
        { value: 'all', text: 'Todos', icon: 'list', count: orders.length },
        { value: 'pending', text: 'Sin controlar', icon: 'pending', tone: 'warning', count: count(this.isPending) },
        { value: 'weighable', text: 'A pesar', icon: 'scale', tone: 'warning', count: count(order => order.weighable_pending > 0) },
        { value: 'short', text: 'Con faltantes', icon: 'trending_down', tone: 'danger', count: count(order => order.short > 0) },
      ]
      if(this.cut && this.cut.invoicing_started_at) options.push({ value: 'error', text: 'Con error', icon: 'error', tone: 'danger', count: count(order => order.invoice_status === 'error') })
      return options
    },
    filteredOrders() {
      if(!this.cut) return []
      const term = this.search.trim().toLowerCase()
      return this.cut.orders.filter(order => {
        if(this.sellerFilter && order.seller_name !== this.sellerFilter) return false
        if(this.view === 'pending' && !this.isPending(order)) return false
        if(this.view === 'weighable' && !order.weighable_pending) return false
        if(this.view === 'short' && !order.short) return false
        if(this.view === 'error' && order.invoice_status !== 'error') return false
        if(term && !String(order.id).includes(term) && !order.customer_name.toLowerCase().includes(term)) return false
        return true
      })
    },
    // El backend ya los devuelve ordenados por vendedor, zona, ciudad y pedido.
    groups() {
      const groups = []
      this.filteredOrders.forEach(order => {
        let group = groups[groups.length - 1]
        if(!group || group.seller !== order.seller_name) {
          group = { seller: order.seller_name, count: 0, ready: 0, weighable: 0, errors: 0, places: [] }
          groups.push(group)
        }
        const key = order.zone_name + '|' + order.city
        let place = group.places[group.places.length - 1]
        if(!place || place.key !== key) {
          place = { key, zone: order.zone_name, city: order.city, orders: [] }
          group.places.push(place)
        }
        place.orders.push(order)
        group.count++
        if(order.confirmed >= order.lines) group.ready++
        if(order.weighable_pending) group.weighable++
        if(order.invoice_status === 'error') group.errors++
      })
      return groups
    },
  },
  watch: {
    cutId: { immediate: true, handler() { this.expanded = {}; this.load() } },
    // La ayuda de la vista muestra el paso en el que está el corte.
    'cut.status'(status) { this.$emit('status', status || '') },
  },
  created() {
    this.refreshTimer = setInterval(() => {
      if(this.cut && !this.busy && (this.invoiceRunning || this.zipRunning)) this.load(true)
    }, 4000)
  },
  beforeDestroy() { clearInterval(this.refreshTimer) },
  methods: {
    statusLabel(status) { return STATUS_LABELS[status] || status },
    formatDate(value) {
      if(!value) return '—'
      const date = new Date(String(value).replace(' ', 'T'))
      return isNaN(date) ? value : date.toLocaleString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    },
    isPending(order) { return order.confirmed < order.lines },
    async load(silent = false) {
      try {
        const response = await serviceAPI.mostrarCorte(this.cutId)
        const wasRunning = this.invoiceRunning
        this.cut = response.data
        if(wasRunning && !this.invoiceRunning) this.$emit('changed')
      } catch(error) {
        if(!silent) this.showError(error)
      }
    },
    applyCut(data, message) {
      this.cut = data
      this.revision++
      this.$emit('changed')
      if(message) this.$awn.success(message)
    },
    toggleOrder(id) { this.$set(this.expanded, id, !this.expanded[id]) },
    toggleSeller(seller) { this.$set(this.collapsedSellers, seller, !this.collapsedSellers[seller]) },
    toggleAllSellers() {
      const collapse = !this.allSellersCollapsed
      const state = {}
      this.groups.forEach(group => { state[group.seller] = collapse })
      this.collapsedSellers = state
    },
    avatar(name) { return Funciones.avatarFromName(name) },
    onLineUpdated(data) {
      const index = this.cut.orders.findIndex(order => order.id === data.order.id)
      if(index >= 0) this.$set(this.cut.orders, index, data.order)
      Object.assign(this.cut, data.state)
    },
    async run(callback) {
      this.busy = true
      try { await callback() } catch(error) { this.showError(error) } finally { this.busy = false }
    },
    async controlAll() {
      const t = this.cut.totals
      const ok = await this.confirmDialog(
        'Se marcan como controlados ' + t.orders_ready + ' pedidos: lo que no se modificó queda con la cantidad pedida.' +
        (t.orders_waiting_weight ? ' Los ' + t.orders_waiting_weight + ' pedidos con productos sin pesar quedan pendientes.' : '') +
        ' Cada pedido se puede reabrir si hay que corregirlo.',
        { title: 'Controlar pedidos', icon: 'done_all', okTitle: 'Controlar' })
      if(!ok) return
      await this.run(async () => {
        const response = await serviceAPI.confirmarPendientesCorte(this.cut.id)
        this.applyCut(response.data, response.data.controlled_now + ' pedidos controlados')
      })
    },
    async controlOrder(order) {
      await this.run(async () => {
        const response = await serviceAPI.confirmarPendientesCorte(this.cut.id, order.id)
        this.applyCut(response.data, 'Pedido #' + order.id + ' controlado')
        this.$set(this.expanded, order.id, false)
      })
    },
    async reopenOrder(order) {
      await this.run(async () => {
        const response = await serviceAPI.reabrirPedidoCorte(this.cut.id, order.id)
        this.applyCut(response.data, 'Pedido #' + order.id + ' reabierto')
        this.$set(this.expanded, order.id, true)
      })
    },
    async complete() {
      const ok = await this.confirmDialog('Los ' + this.cut.totals.orders + ' pedidos controlados pasan a pendientes de envío y quedan listos para facturar. Podés reabrir la preparación mientras no se facture.',
        { title: 'Finalizar preparación', icon: 'task_alt', okTitle: 'Finalizar' })
      if(!ok) return
      await this.run(async () => {
        const response = await serviceAPI.cerrarPreparacion(this.cut.id)
        this.applyCut(response.data, 'Preparación finalizada')
      })
    },
    async reopen() {
      await this.run(async () => {
        const response = await serviceAPI.reabrirPreparacion(this.cut.id)
        this.applyCut(response.data, 'Preparación reabierta')
      })
    },
    async invoice() {
      const retry = this.cut.totals.invoices_error > 0
      const pending = this.cut.totals.orders - this.cut.totals.invoices_done
      const ok = await this.confirmDialog(
        (retry ? 'Se reintentan ' + pending + ' pedidos sin factura.' : 'Se genera una factura por pedido (' + pending + ') con el punto de venta de cada uno.') +
        ' El proceso corre en segundo plano y un pedido con error no frena a los demás.',
        { title: retry ? 'Reintentar facturación' : 'Facturar corte', icon: 'receipt_long', okTitle: retry ? 'Reintentar' : 'Facturar' })
      if(!ok) return
      await this.run(async () => {
        const response = await serviceAPI.facturarCorte(this.cut.id)
        this.applyCut(response.data, 'Facturación iniciada')
      })
    },
    async rebuildZip() {
      await this.run(async () => {
        const response = await serviceAPI.regenerarZipCorte(this.cut.id)
        this.applyCut(response.data, 'Generando ZIP')
      })
    },
    async downloadZip() {
      await this.run(async () => {
        try {
          const response = await serviceAPI.descargarZipCorte(this.cut.id)
          const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/zip' }))
          const link = document.createElement('a')
          link.href = url
          link.download = 'corte-' + this.cut.id + '-comprobantes.zip'
          document.body.appendChild(link)
          link.click()
          link.remove()
          setTimeout(() => window.URL.revokeObjectURL(url), 1000)
        } catch(error) {
          // La respuesta de error llega como Blob por el responseType de la descarga.
          if(error.response && error.response.data instanceof Blob) {
            const text = await error.response.data.text()
            try { error.response.data = JSON.parse(text) } catch(e) { error.response.data = text }
          }
          throw error
        }
      })
    },
    async removeOrder(order) {
      const ok = await this.confirmDialog(
        'El pedido #' + order.id + ' (' + order.customer_name + ') vuelve a quedar cerrado y disponible para un próximo corte. Se recupera el stock que se había ajustado en la preparación.',
        { title: 'Quitar del corte', icon: 'remove_shopping_cart', danger: true, okTitle: 'Quitar' })
      if(!ok) return
      await this.run(async () => {
        const response = await serviceAPI.quitarPedidoDelCorte(this.cut.id, order.id)
        this.$delete(this.expanded, order.id)
        this.applyCut(response.data, 'Pedido #' + order.id + ' quitado del corte')
      })
    },
    async removeCut() {
      const ok = await this.confirmDialog('Los ' + this.cut.totals.orders + ' pedidos vuelven a quedar cerrados y disponibles para otro corte.',
        { title: 'Eliminar corte #' + this.cut.id, danger: true, okTitle: 'Eliminar' })
      if(!ok) return
      await this.run(async () => {
        await serviceAPI.eliminarCorte(this.cut.id)
        this.$awn.success('Corte eliminado')
        this.$emit('deleted')
      })
    },
    openAddOrders() {
      this.addModal.open = true
      this.addModal.search = ''
      this.addModal.selected = []
      this.loadEligible(1)
    },
    async loadEligible(page = 1) {
      this.addModal.loading = true
      try {
        const response = await serviceAPI.pedidosParaCorte({ page, search: this.addModal.search || null })
        this.addModal.items = response.data.data || []
        this.addModal.total = response.data.total || 0
        this.addModal.perPage = response.data.per_page || 50
        this.addModal.page = response.data.current_page || page
      } catch(error) {
        this.showError(error)
      } finally {
        this.addModal.loading = false
      }
    },
    toggleAddSelection(item) {
      const selected = this.addModal.selected
      this.addModal.selected = selected.includes(item.id) ? selected.filter(id => id !== item.id) : selected.concat(item.id)
    },
    async addOrders() {
      await this.run(async () => {
        const total = this.addModal.selected.length
        const response = await serviceAPI.agregarPedidosAlCorte(this.cut.id, this.addModal.selected)
        this.addModal.open = false
        this.applyCut(response.data, total === 1 ? 'Pedido agregado' : total + ' pedidos agregados')
      })
    },
    confirmDialog(message, options) {
      const danger = !!options.danger
      const title = this.$createElement('span', { class: 'app-confirm-header__title' }, [
        this.$createElement('AppIcon', { props: { name: options.icon || (danger ? 'warning' : 'help') } }),
        options.title,
      ])
      return this.$bvModal.msgBoxConfirm(message, {
        title, size: 'sm', buttonSize: 'sm',
        okVariant: danger ? 'danger' : 'dark', okTitle: options.okTitle, cancelTitle: 'Cancelar',
        footerClass: 'p-2', headerClass: danger ? 'app-confirm-header app-confirm-header--danger' : 'app-confirm-header',
        hideHeaderClose: false, centered: true,
      })
    },
    showError(error) { this.$awn.alert(Error.showError(error)) },
  },
}
</script>

<style scoped>
.cut-workspace >>> .cut-workspace__header-body { padding: 0.9rem 1.1rem; }
.cut-workspace__header { display: flex; align-items: flex-start; justify-content: space-between; gap: 1rem; flex-wrap: wrap; }
.cut-workspace__title { display: flex; align-items: flex-start; gap: 0.5rem; }
.cut-workspace__title h4 { display: flex; align-items: center; gap: 0.6rem; font-size: 1.15rem; font-weight: 700; }
.cut-workspace__meta { margin-top: 0.15rem; font-size: 0.78rem; color: var(--app-color-text-muted, #687684); }
.cut-workspace__header-actions { display: flex; align-items: center; gap: 0.5rem; }
.cut-workspace__status { padding: 0.1rem 0.5rem; border-radius: 1rem; font-size: 0.7rem; font-weight: 700; color: var(--app-color-text-secondary, #5d6872); background: var(--app-color-surface-subtle, #f1f3f5); }
.cut-workspace__status--preparing { color: #8a5a00; background: var(--app-color-warning-soft, #fff8e6); }
.cut-workspace__status--prepared { color: var(--app-color-primary, #2b5f8a); background: var(--app-color-primary-soft, #eef4fa); }
.cut-workspace__status--ready, .cut-workspace__status--on_route { color: var(--app-color-success, #1e7b4a); background: var(--app-color-success-soft, #eef8f2); }

.cut-steps { display: flex; gap: 0.5rem; margin: 1rem 0 0; padding: 0.85rem 0 0; list-style: none; border-top: 1px solid var(--app-color-border, #dee2e6); }
.cut-steps__item { display: flex; align-items: center; gap: 0.55rem; flex: 1 1 0; min-width: 0; }
.cut-steps__item + .cut-steps__item::before { content: ''; flex: 0 0 1.2rem; height: 1px; background: var(--app-color-border, #dee2e6); }
.cut-steps__marker { display: inline-flex; flex-shrink: 0; align-items: center; justify-content: center; width: 1.7rem; height: 1.7rem; border: 1px solid var(--app-color-border, #dee2e6); border-radius: 50%; font-size: 0.78rem; font-weight: 700; color: var(--app-color-text-muted, #687684); background: var(--app-color-surface, #fff); }
.cut-steps__text { display: flex; flex-direction: column; min-width: 0; line-height: 1.2; }
.cut-steps__text strong { font-size: 0.8rem; color: var(--app-color-text-secondary, #5d6872); }
.cut-steps__text small { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: 0.72rem; color: var(--app-color-text-muted, #687684); }
.cut-steps__item--current .cut-steps__marker { border-color: var(--app-color-text, #1c2833); color: var(--app-color-on-primary, #fff); background: var(--app-color-text, #1c2833); }
.cut-steps__item--current .cut-steps__text strong { color: var(--app-color-text, #1c2833); }
.cut-steps__item--done .cut-steps__marker { border-color: var(--app-color-success-border, #b7e0c8); color: var(--app-color-success, #1e7b4a); background: var(--app-color-success-soft, #eef8f2); }

.cut-workspace__orders { overflow: hidden; }
.cut-workspace__check { display: inline-flex; padding: 0.15rem; font-size: 1.25rem; cursor: pointer; }
.cut-workspace__toolbar { display: flex; align-items: center; flex-wrap: wrap; gap: 0.5rem; padding: 0.7rem 0.75rem; border-bottom: 1px solid var(--app-color-border, #dee2e6); }
.cut-workspace__search { flex: 1 1 220px; max-width: 320px; }
.cut-workspace__seller { flex: 0 1 200px; }
.cut-workspace__views { display: inline-flex; flex-wrap: wrap; gap: 0.3rem; }
.cut-workspace__view { padding: 0.2rem 0.6rem; border: 1px solid var(--app-color-border, #dee2e6); border-radius: 1rem; font-size: 0.75rem; color: var(--app-color-text-secondary, #5d6872); background: var(--app-color-surface, #fff); }
.cut-workspace__view { display: inline-flex; align-items: center; gap: 0.25rem; }
.cut-workspace__view .app-icon { font-size: 0.95rem; }
.cut-workspace__view-count { margin-left: 0.1rem; padding: 0 0.35rem; border-radius: 1rem; font-weight: 700; background: var(--app-color-surface-subtle, #f1f3f5); }
.cut-workspace__view-count--warning { color: #8a5a00; background: var(--app-color-warning-soft, #fff8e6); }
.cut-workspace__view-count--danger { color: var(--app-color-danger, #b42318); background: var(--app-color-danger-soft, #fdf0ef); }
.cut-workspace__view--active .cut-workspace__view-count { color: var(--app-color-text, #1c2833); background: var(--app-color-on-primary, #fff); }
.cut-workspace__fold { display: inline-flex; gap: 0.75rem; }
.cut-workspace__fold .btn { padding: 0; }
.cut-workspace__view--active { border-color: var(--app-color-text, #1c2833); color: var(--app-color-on-primary, #fff); background: var(--app-color-text, #1c2833); }

.cut-group__seller { display: flex; align-items: center; gap: 0.55rem; padding: 0.5rem 0.75rem; font-size: 0.88rem; font-weight: 700; color: var(--app-color-text, #1c2833); background: var(--app-color-surface-subtle, #f1f3f5); border-bottom: 1px solid var(--app-color-border, #dee2e6); cursor: pointer; user-select: none; }
.cut-group__seller:hover { background: var(--app-color-primary-soft, #eef4fa); }
.cut-group__chevron { color: var(--app-color-text-muted, #687684); }
.cut-group__name { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.cut-group__summary { display: inline-flex; gap: 0.35rem; margin-left: auto; }
.cut-group__pill { display: inline-flex; align-items: center; gap: 0.2rem; padding: 0.05rem 0.45rem; border-radius: 1rem; font-size: 0.72rem; font-weight: 600; white-space: nowrap; color: var(--app-color-text-secondary, #5d6872); background: var(--app-color-surface, #fff); border: 1px solid var(--app-color-border, #dee2e6); }
.cut-group__pill .app-icon { font-size: 0.9rem; }
.cut-group__pill--success { color: var(--app-color-success, #1e7b4a); border-color: var(--app-color-success-border, #b7e0c8); background: var(--app-color-success-soft, #eef8f2); }
.cut-group__pill--warning { color: #8a5a00; border-color: var(--app-color-warning-border, #f0d48a); background: var(--app-color-warning-soft, #fff8e6); }
.cut-group__pill--danger { color: var(--app-color-danger, #b42318); border-color: var(--app-color-danger-border, #f3c2bd); background: var(--app-color-danger-soft, #fdf0ef); }

.cut-kpis { display: grid; grid-template-columns: repeat(5, minmax(0, 1fr)); gap: 0.6rem; margin-bottom: 1rem; }
.cut-kpi { display: flex; align-items: center; gap: 0.65rem; padding: 0.7rem 0.8rem; border: 1px solid var(--app-color-border, #dee2e6); border-radius: 0.5rem; background: var(--app-color-surface, #fff); }
.cut-kpi__icon { display: inline-flex; flex-shrink: 0; align-items: center; justify-content: center; width: 2.3rem; height: 2.3rem; border-radius: 0.5rem; font-size: 1.3rem; color: var(--app-color-text-secondary, #5d6872); background: var(--app-color-surface-subtle, #f1f3f5); }
.cut-kpi__body { display: flex; flex-direction: column; min-width: 0; flex: 1 1 auto; }
.cut-kpi__label { font-size: 0.7rem; font-weight: 600; letter-spacing: 0.03em; text-transform: uppercase; color: var(--app-color-text-muted, #687684); }
.cut-kpi__value { font-size: 1.15rem; font-variant-numeric: tabular-nums; color: var(--app-color-text, #1c2833); }
.cut-kpi__bar { display: block; height: 0.25rem; margin-top: 0.25rem; overflow: hidden; border-radius: 1rem; background: var(--app-color-border, #dee2e6); }
.cut-kpi__bar span { display: block; height: 100%; background: currentColor; }
.cut-kpi--primary .cut-kpi__icon { color: var(--app-color-primary, #2b5f8a); background: var(--app-color-primary-soft, #eef4fa); }
.cut-kpi--primary .cut-kpi__bar { color: var(--app-color-primary, #2b5f8a); }
.cut-kpi--success .cut-kpi__icon { color: var(--app-color-success, #1e7b4a); background: var(--app-color-success-soft, #eef8f2); }
.cut-kpi--success .cut-kpi__bar { color: var(--app-color-success, #1e7b4a); }
.cut-kpi--warning .cut-kpi__icon { color: #8a5a00; background: var(--app-color-warning-soft, #fff8e6); }
.cut-kpi--warning { border-color: var(--app-color-warning-border, #f0d48a); }
.cut-kpi--danger .cut-kpi__icon { color: var(--app-color-danger, #b42318); background: var(--app-color-danger-soft, #fdf0ef); }
.cut-kpi--danger { border-color: var(--app-color-danger-border, #f3c2bd); }
.cut-kpi--danger .cut-kpi__value { color: var(--app-color-danger, #b42318); }
.cut-group__place { display: flex; align-items: center; gap: 0.3rem; padding: 0.45rem 0.75rem 0.3rem; font-size: 0.72rem; font-weight: 700; letter-spacing: 0.04em; text-transform: uppercase; color: var(--app-color-text-secondary, #5d6872); border-bottom: 1px solid var(--app-color-border, #dee2e6); }
.cut-group__place span { color: var(--app-color-text-muted, #9aa5b1); }

@media (max-width: 991.98px) {
  .cut-kpis { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
@media (max-width: 767.98px) {
  .cut-group__summary { display: none; }
  .cut-workspace__header-actions { width: 100%; }
  .cut-workspace__header-actions .btn-dark { flex: 1 1 auto; }
  .cut-steps { flex-direction: column; gap: 0.4rem; }
  .cut-steps__item + .cut-steps__item::before { display: none; }
  .cut-workspace__search, .cut-workspace__seller { flex: 1 1 100%; max-width: none; }
}
</style>
